import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Trash2, Save, Play, ArrowLeft, Zap, AlertCircle, 
  CheckCircle, Building2, RefreshCw, Copy, ChevronDown, ChevronUp,
  GitBranch, Target, Settings, Mail, MessageCircle, Phone, 
  Users, User, Clock, Tag, Bell, Edit3, Link, Sparkles,
  FileText, Calendar, CheckSquare, DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for triggers and actions
const iconMap = {
  'user-plus': User,
  'target': Target,
  'edit': Edit3,
  'dollar-sign': DollarSign,
  'git-branch': GitBranch,
  'check-circle': CheckCircle,
  'phone': Phone,
  'phone-call': Phone,
  'message-circle': MessageCircle,
  'file-text': FileText,
  'calendar': Calendar,
  'check-square': CheckSquare,
  'clock': Clock,
  'mail': Mail,
  'smartphone': Phone,
  'user': User,
  'users': Users,
  'refresh-cw': RefreshCw,
  'edit-3': Edit3,
  'tag': Tag,
  'bell': Bell,
  'link': Link,
  'sparkles': Sparkles,
  'settings': Settings
};

// Color classes for modules - light theme
const moduleColors = {
  CRM: 'bg-blue-100 text-blue-700 border-blue-200',
  REAL_ESTATE: 'bg-green-100 text-green-700 border-green-200',
  MARKETING: 'bg-purple-100 text-purple-700 border-purple-200',
  COMMUNICATION: 'bg-amber-100 text-amber-700 border-amber-200',
  OPERATIONS: 'bg-slate-100 text-slate-700 border-slate-200'
};

// ============ CONDITION ROW COMPONENT ============
const ConditionRow = ({ rule, index, fields, operators, onUpdate, onRemove, dynamicData }) => {
  const field = fields.find(f => f.id === rule.field);
  const availableOperators = operators.filter(op => 
    !field?.type || op.types.includes(field.type)
  );
  
  const getFieldOptions = () => {
    if (field?.dynamic === 'users') {
      return dynamicData?.users || [];
    }
    if (field?.dynamic === 'teams') {
      return dynamicData?.teams || [];
    }
    return field?.options || [];
  };
  
  return (
    <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-lg" data-testid={`condition-row-${index}`}>
      <Select 
        value={rule.field} 
        onValueChange={(v) => onUpdate(index, { ...rule, field: v })}
      >
        <SelectTrigger className="w-[180px] bg-white border-slate-200">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200">
          {fields.map(f => (
            <SelectItem key={f.id} value={f.id}>
              {f.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      <Select 
        value={rule.operator} 
        onValueChange={(v) => onUpdate(index, { ...rule, operator: v })}
      >
        <SelectTrigger className="w-[160px] bg-white border-slate-200">
          <SelectValue placeholder="Operator" />
        </SelectTrigger>
        <SelectContent className="bg-white border-slate-200">
          {availableOperators.map(op => (
            <SelectItem key={op.id} value={op.id}>
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      
      {!['is_null', 'is_not_null'].includes(rule.operator) && (
        field?.type === 'select' || field?.dynamic ? (
          <Select 
            value={rule.value} 
            onValueChange={(v) => onUpdate(index, { ...rule, value: v })}
          >
            <SelectTrigger className="w-[180px] bg-white border-slate-200">
              <SelectValue placeholder="Select value" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200">
              {getFieldOptions().map(opt => (
                <SelectItem key={opt.id || opt} value={opt.id || opt}>
                  {opt.name || opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={rule.value || ''}
            onChange={(e) => onUpdate(index, { ...rule, value: e.target.value })}
            placeholder="Enter value"
            className="w-[180px] bg-white border-slate-200"
          />
        )
      )}
      
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => onRemove(index)}
        className="text-red-500 hover:text-red-700"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

// ============ CONDITION GROUP COMPONENT ============
const ConditionGroup = ({ group, groupIndex, fields, operators, onChange, onRemove, dynamicData }) => {
  const handleRuleUpdate = (ruleIndex, updatedRule) => {
    const newRules = [...group.rules];
    newRules[ruleIndex] = updatedRule;
    onChange({ ...group, rules: newRules });
  };
  
  const handleRuleRemove = (ruleIndex) => {
    const newRules = group.rules.filter((_, i) => i !== ruleIndex);
    if (newRules.length === 0) {
      onRemove();
    } else {
      onChange({ ...group, rules: newRules });
    }
  };
  
  const addRule = () => {
    onChange({
      ...group,
      rules: [...group.rules, { field: '', operator: 'equals', value: '' }]
    });
  };
  
  return (
    <Card className="bg-slate-50 border-slate-200" data-testid={`condition-group-${groupIndex}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Select 
              value={group.group_type} 
              onValueChange={(v) => onChange({ ...group, group_type: v })}
            >
              <SelectTrigger className="w-[100px] bg-white border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="AND">AND</SelectItem>
                <SelectItem value="OR">OR</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-600">Match</span>
            <Badge variant="outline" className="text-slate-600 border-slate-300">
              {group.group_type === 'AND' ? 'All conditions' : 'Any condition'}
            </Badge>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onRemove}
            className="text-red-500 hover:text-red-700"
          >
            Remove Group
          </Button>
        </div>
        
        <div className="space-y-2">
          {group.rules.map((rule, ruleIndex) => (
            <ConditionRow
              key={ruleIndex}
              rule={rule}
              index={ruleIndex}
              fields={fields}
              operators={operators}
              onUpdate={handleRuleUpdate}
              onRemove={handleRuleRemove}
              dynamicData={dynamicData}
            />
          ))}
        </div>
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={addRule}
          className="mt-3 border-slate-200 text-slate-700 hover:bg-slate-100"
          data-testid={`add-condition-btn-${groupIndex}`}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Condition
        </Button>
      </CardContent>
    </Card>
  );
};

// ============ ACTION CARD COMPONENT ============
const ActionCard = ({ action, index, actionConfig, onUpdate, onRemove, onMoveUp, onMoveDown, dynamicData, isFirst, isLast }) => {
  const config = actionConfig[action.type] || {};
  const Icon = iconMap[config.icon] || Settings;
  const [expanded, setExpanded] = useState(true);
  
  const handleParamChange = (paramId, value) => {
    onUpdate(index, {
      ...action,
      params: { ...action.params, [paramId]: value }
    });
  };
  
  const renderField = (field) => {
    // Check conditional visibility
    if (field.condition) {
      const condValue = action.params?.[field.condition.field];
      if (condValue !== field.condition.value) {
        return null;
      }
    }
    
    const value = action.params?.[field.id] || field.default || '';
    
    if (field.type === 'select') {
      let options = field.options || [];
      if (field.dynamic === 'users') {
        options = dynamicData?.users || [];
      } else if (field.dynamic === 'teams') {
        options = dynamicData?.teams || [];
      }
      
      return (
        <div key={field.id} className="space-y-1">
          <Label className="text-xs text-slate-400">{field.label}</Label>
          <Select value={value} onValueChange={(v) => handleParamChange(field.id, v)}>
            <SelectTrigger className="bg-slate-700 border-slate-600">
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.id || opt} value={opt.id || opt}>
                  {opt.name || opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (field.type === 'textarea') {
      return (
        <div key={field.id} className="space-y-1">
          <Label className="text-xs text-slate-600">{field.label}</Label>
          <Textarea
            value={value}
            onChange={(e) => handleParamChange(field.id, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            className="bg-white border-slate-200 min-h-[80px]"
          />
        </div>
      );
    }
    
    return (
      <div key={field.id} className="space-y-1">
        <Label className="text-xs text-slate-600">{field.label}</Label>
        <Input
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => handleParamChange(field.id, field.type === 'number' ? parseInt(e.target.value) : e.target.value)}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          className="bg-white border-slate-200"
        />
      </div>
    );
  };
  
  return (
    <Card className="bg-slate-50 border-slate-200" data-testid={`action-card-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-5 px-1 text-slate-500 hover:text-slate-700"
                onClick={() => onMoveUp(index)}
                disabled={isFirst}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-5 px-1 text-slate-500 hover:text-slate-700"
                onClick={() => onMoveDown(index)}
                disabled={isLast}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            <Badge variant="outline" className="h-6 w-6 p-0 flex items-center justify-center border-slate-300 text-slate-600">
              {index + 1}
            </Badge>
            <div className="p-2 rounded-lg bg-white border border-slate-200">
              <Icon className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900 text-sm">{config.name || action.type}</p>
              <p className="text-xs text-slate-500">{config.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {config.requires_integration && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                Requires {config.requires_integration}
              </Badge>
            )}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-slate-600 hover:text-slate-900"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => onRemove(index)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {expanded && config.fields && config.fields.length > 0 && (
          <div className="mt-4 grid gap-3 pl-12">
            {config.fields.map(renderField)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============ TRIGGER SELECTOR COMPONENT ============
const TriggerSelector = ({ triggers, selectedTrigger, onSelect, enabledModules }) => {
  const triggersByModule = {};
  
  Object.entries(triggers).forEach(([key, trigger]) => {
    const module = trigger.module || 'OTHER';
    if (!triggersByModule[module]) {
      triggersByModule[module] = [];
    }
    triggersByModule[module].push({ ...trigger, key });
  });
  
  return (
    <div className="space-y-4">
      {Object.entries(triggersByModule).map(([module, moduleTriggers]) => (
        <div key={module}>
          <h4 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
            <Badge className={moduleColors[module] || 'bg-slate-100 text-slate-600'}>
              {module}
            </Badge>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {moduleTriggers.map(trigger => {
              const Icon = iconMap[trigger.icon] || Zap;
              const isSelected = selectedTrigger?.type === trigger.key;
              
              return (
                <button
                  key={trigger.key}
                  onClick={() => onSelect({ type: trigger.key, params: {} })}
                  className={`p-3 rounded-lg border transition-all text-left ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
                  }`}
                  data-testid={`trigger-${trigger.key}`}
                >
                  <div className="flex items-center gap-2">
                    <Icon className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-slate-500'}`} />
                    <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-slate-900'}`}>
                      {trigger.name}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{trigger.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ ACTION SELECTOR DIALOG ============
const ActionSelectorDialog = ({ open, onOpenChange, actions, onSelect, integrations }) => {
  const actionsByCategory = {};
  
  Object.entries(actions).forEach(([key, action]) => {
    const category = action.category || 'other';
    if (!actionsByCategory[category]) {
      actionsByCategory[category] = [];
    }
    actionsByCategory[category].push({ ...action, key });
  });
  
  const categoryNames = {
    communication: 'Communication',
    assignment: 'Assignment',
    data: 'Data Updates',
    operations: 'Operations',
    flow: 'Flow Control',
    integration: 'Integrations',
    ai: 'AI & Automation'
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-white" data-testid="action-selector-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Zap className="h-5 w-5 text-blue-600" />
            Add Action
          </DialogTitle>
          <DialogDescription>
            Choose an action to add to your workflow
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6">
            {Object.entries(actionsByCategory).map(([category, categoryActions]) => (
              <div key={category}>
                <h4 className="text-sm font-medium text-slate-500 mb-2 uppercase tracking-wide">
                  {categoryNames[category] || category}
                </h4>
                <div className="grid gap-2">
                  {categoryActions.map(action => {
                    const Icon = iconMap[action.icon] || Settings;
                    const isConfigured = !action.requires_integration || 
                      integrations?.[action.requires_integration]?.configured;
                    
                    return (
                      <button
                        key={action.key}
                        onClick={() => {
                          onSelect({ type: action.key, params: {} });
                          onOpenChange(false);
                        }}
                        className={`p-3 rounded-lg border transition-all text-left flex items-center gap-3 ${
                          isConfigured 
                            ? 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100' 
                            : 'border-amber-200 bg-amber-50 hover:border-amber-300'
                        }`}
                        data-testid={`action-option-${action.key}`}
                      >
                        <div className="p-2 rounded-lg bg-white border border-slate-200">
                          <Icon className="h-4 w-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-900">{action.name}</span>
                            {!isConfigured && (
                              <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                Not Configured
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">{action.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// ============ MAIN WORKFLOW BUILDER COMPONENT ============
const WorkflowBuilder = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { workflowId } = useParams();
  
  const isEditing = !!workflowId;
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [showActionSelector, setShowActionSelector] = useState(false);
  
  // Workflow state
  const [workflow, setWorkflow] = useState({
    name: '',
    description: '',
    organization_id: '',
    module_name: 'CRM',
    trigger: null,
    conditions: [],
    actions: [],
    is_active: false
  });
  
  const [validationErrors, setValidationErrors] = useState([]);
  
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Fetch config and organizations
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configRes, orgsRes] = await Promise.all([
          fetch(`${API_URL}/api/workflow-builder/config?token=${token}`),
          fetch(`${API_URL}/api/workflow-builder/organizations?token=${token}`)
        ]);
        
        const configData = await configRes.json();
        const orgsData = await orgsRes.json();
        
        if (configData.success) {
          setConfig(configData);
        }
        
        if (orgsData.success) {
          setOrganizations(orgsData.organizations || []);
          // Set default organization for non-super admins
          if (!isSuperAdmin && orgsData.organizations?.length === 1) {
            setWorkflow(prev => ({ ...prev, organization_id: orgsData.organizations[0].id }));
          }
        }
        
        // Load existing workflow if editing
        if (isEditing) {
          const workflowRes = await fetch(`${API_URL}/api/workflow-builder/workflows/${workflowId}?token=${token}`);
          const workflowData = await workflowRes.json();
          
          if (workflowData.success && workflowData.workflow) {
            setWorkflow(workflowData.workflow);
          } else {
            toast.error('Workflow not found');
            navigate('/workflows-builder');
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load workflow configuration');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [token, workflowId, isEditing, navigate, isSuperAdmin]);
  
  // Reload config when organization changes
  const reloadConfigForOrg = useCallback(async (orgId) => {
    if (!orgId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/workflow-builder/config?token=${token}&organization_id=${orgId}`);
      const data = await res.json();
      if (data.success) {
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to reload config:', error);
    }
  }, [token]);
  
  // Handle organization change
  const handleOrgChange = (orgId) => {
    setWorkflow(prev => ({ ...prev, organization_id: orgId }));
    reloadConfigForOrg(orgId);
  };
  
  // Condition handlers
  const addConditionGroup = () => {
    setWorkflow(prev => ({
      ...prev,
      conditions: [...prev.conditions, { group_type: 'AND', rules: [{ field: '', operator: 'equals', value: '' }] }]
    }));
  };
  
  const updateConditionGroup = (index, group) => {
    const newConditions = [...workflow.conditions];
    newConditions[index] = group;
    setWorkflow(prev => ({ ...prev, conditions: newConditions }));
  };
  
  const removeConditionGroup = (index) => {
    setWorkflow(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
  };
  
  // Action handlers
  const addAction = (action) => {
    setWorkflow(prev => ({
      ...prev,
      actions: [...prev.actions, action]
    }));
  };
  
  const updateAction = (index, action) => {
    const newActions = [...workflow.actions];
    newActions[index] = action;
    setWorkflow(prev => ({ ...prev, actions: newActions }));
  };
  
  const removeAction = (index) => {
    setWorkflow(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };
  
  const moveAction = (index, direction) => {
    const newActions = [...workflow.actions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
    setWorkflow(prev => ({ ...prev, actions: newActions }));
  };
  
  // Validation
  const validate = () => {
    const errors = [];
    
    if (!workflow.name?.trim()) {
      errors.push('Workflow name is required');
    }
    
    if (!workflow.organization_id) {
      errors.push('Organization is required');
    }
    
    if (!workflow.trigger?.type) {
      errors.push('Trigger is required');
    }
    
    if (workflow.actions.length === 0) {
      errors.push('At least one action is required');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };
  
  // Save workflow
  const handleSave = async (activate = false) => {
    if (!validate()) {
      toast.error('Please fix validation errors');
      return;
    }
    
    setSaving(true);
    
    try {
      const url = isEditing 
        ? `${API_URL}/api/workflow-builder/workflows/${workflowId}?token=${token}`
        : `${API_URL}/api/workflow-builder/workflows?token=${token}`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...workflow,
          is_active: activate
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(isEditing ? 'Workflow updated' : 'Workflow created');
        navigate('/workflows-builder');
      } else {
        toast.error(data.error || data.detail || 'Failed to save workflow');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };
  
  // Test workflow
  const handleTest = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors before testing');
      return;
    }
    
    // Show test dialog
    toast.info('Test workflow feature coming soon');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="workflow-builder">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/workflows-builder')}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEditing ? 'Edit Workflow' : 'Create Workflow'}
              </h1>
              <p className="text-slate-600 text-sm">
                Build automated workflows with triggers, conditions, and actions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleTest}
              disabled={saving}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="test-workflow-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              Test
            </Button>
            <Button 
              variant="outline" 
              onClick={() => handleSave(false)}
              disabled={saving}
              data-testid="save-draft-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button 
              onClick={() => handleSave(true)}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-activate-btn"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Save & Activate
            </Button>
          </div>
        </div>
        
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <Card className="bg-red-50 border-red-200 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-medium text-red-700">Please fix the following errors:</p>
                  <ul className="list-disc list-inside text-sm text-red-600 mt-1">
                    {validationErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Basic Info */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700">Workflow Name *</Label>
                <Input
                  value={workflow.name}
                  onChange={(e) => setWorkflow(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., New Contact AI Call"
                  className="bg-white border-slate-200"
                  data-testid="workflow-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-700">Organization *</Label>
                <Select 
                  value={workflow.organization_id} 
                  onValueChange={handleOrgChange}
                  disabled={!isSuperAdmin && organizations.length <= 1}
                >
                  <SelectTrigger className="bg-white border-slate-200" data-testid="org-select">
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-700">Description</Label>
              <Textarea
                value={workflow.description}
                onChange={(e) => setWorkflow(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this workflow does"
                className="bg-white border-slate-200"
                data-testid="workflow-desc-input"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-700">Module</Label>
                <Select 
                  value={workflow.module_name} 
                  onValueChange={(v) => setWorkflow(prev => ({ ...prev, module_name: v }))}
                >
                  <SelectTrigger className="w-[200px] bg-white border-slate-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {config?.enabled_modules?.map(mod => (
                      <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                    )) || (
                      <>
                        <SelectItem value="CRM">CRM</SelectItem>
                        <SelectItem value="REAL_ESTATE">Real Estate</SelectItem>
                        <SelectItem value="COMMUNICATION">Communication</SelectItem>
                        <SelectItem value="OPERATIONS">Operations</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={workflow.is_active}
                  onCheckedChange={(v) => setWorkflow(prev => ({ ...prev, is_active: v }))}
                  data-testid="active-toggle"
                />
                <Label className="text-slate-700">Active</Label>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Trigger Section */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <Zap className="h-5 w-5 text-amber-500" />
              Trigger *
            </CardTitle>
            <CardDescription>Choose when this workflow should run</CardDescription>
          </CardHeader>
          <CardContent>
            {config?.triggers && (
              <TriggerSelector
                triggers={config.triggers}
                selectedTrigger={workflow.trigger}
                onSelect={(trigger) => setWorkflow(prev => ({ ...prev, trigger }))}
                enabledModules={config.enabled_modules}
              />
            )}
          </CardContent>
        </Card>
        
        {/* Conditions Section */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <GitBranch className="h-5 w-5 text-purple-500" />
              Conditions
              <Badge variant="outline" className="text-slate-500 border-slate-300">Optional</Badge>
            </CardTitle>
            <CardDescription>Define when this workflow should execute (IF/THEN logic)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {workflow.conditions.map((group, index) => (
                <ConditionGroup
                  key={index}
                  group={group}
                  groupIndex={index}
                  fields={config?.condition_fields || []}
                  operators={config?.operators || []}
                  onChange={(g) => updateConditionGroup(index, g)}
                  onRemove={() => removeConditionGroup(index)}
                  dynamicData={config?.dynamic_data}
                />
              ))}
              
              <Button 
                variant="outline" 
                onClick={addConditionGroup}
                className="w-full border-slate-200 text-slate-700 hover:bg-slate-50"
                data-testid="add-condition-group-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Condition Group
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Actions Section */}
        <Card className="bg-white border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
              <Settings className="h-5 w-5 text-blue-500" />
              Actions *
            </CardTitle>
            <CardDescription>Define what happens when the workflow runs (executed in order)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {workflow.actions.map((action, index) => (
                <ActionCard
                  key={index}
                  action={action}
                  index={index}
                  actionConfig={config?.actions || {}}
                  onUpdate={updateAction}
                  onRemove={removeAction}
                  onMoveUp={() => moveAction(index, 'up')}
                  onMoveDown={() => moveAction(index, 'down')}
                  dynamicData={config?.dynamic_data}
                  isFirst={index === 0}
                  isLast={index === workflow.actions.length - 1}
                />
              ))}
              
              <Button 
                variant="outline" 
                onClick={() => setShowActionSelector(true)}
                className="w-full"
                data-testid="add-action-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Action
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Integration Warnings */}
        {config?.integrations && Object.entries(config.integrations).some(([k, v]) => !v.configured) && (
          <Card className="bg-amber-500/10 border-amber-500/30 mb-6">
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-400">Some integrations are not configured</p>
                  <p className="text-sm text-amber-300/80 mt-1">
                    Actions requiring these integrations may not work:
                  </p>
                  <div className="flex gap-2 mt-2">
                    {Object.entries(config.integrations)
                      .filter(([k, v]) => !v.configured)
                      .map(([key, value]) => (
                        <Badge key={key} variant="outline" className="text-amber-400 border-amber-400/30">
                          {value.name}
                        </Badge>
                      ))
                    }
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Action Selector Dialog */}
      <ActionSelectorDialog
        open={showActionSelector}
        onOpenChange={setShowActionSelector}
        actions={config?.actions || {}}
        onSelect={addAction}
        integrations={config?.integrations}
      />
    </div>
  );
};

export default WorkflowBuilder;
